import { supabase } from './supabase'
import { differenceInMonths, addMonths, isAfter, isBefore, format } from 'date-fns'

export interface VestingSchedule {
  id: string
  schedule_name: string
  duration_months: number
  cliff_months: number | null
  vesting_frequency: string | null
}

export interface VestingStatus {
  user_id: string
  user_name: string
  email: string
  total_shares: number
  vested_shares: number
  unvested_shares: number
  vesting_percentage: number
  grant_date: string
  vesting_start_date: string
  cliff_date: string
  cliff_reached: boolean
  days_until_cliff: number | null
  next_vesting_date: string | null
  vesting_end_date: string
  is_fully_vested: boolean
  vesting_schedule: VestingSchedule
  monthly_vesting_rate: number
  accelerated_vesting?: {
    trigger_event: string
    acceleration_percentage: number
    additional_vested_shares: number
  }
}

export interface VestingMilestone {
  date: string
  shares_vesting: number
  cumulative_vested_shares: number
  percentage_vested: number
  is_cliff: boolean
  is_completed: boolean
}

export class VestingCalculator {
  private projectId: string

  constructor(projectId: string) {
    this.projectId = projectId
  }

  async calculateVestingStatus(userId?: string): Promise<VestingStatus[]> {
    // Fetch equity offers with vesting schedules
    const query = supabase
      .from('equity_offers')
      .select(`
        *,
        users!equity_offers_user_id_fkey (
          id,
          email,
          first_name,
          last_name
        ),
        vesting_schedules (
          id,
          schedule_name,
          duration_months,
          cliff_months,
          vesting_frequency
        )
      `)
      .eq('project_id', this.projectId)
      .eq('status', 'accepted')
      .not('vesting_schedule_id', 'is', null)

    if (userId) {
      query.eq('user_id', userId)
    }

    const { data: offers, error } = await query

    if (error) throw error

    const today = new Date()
    const vestingStatuses: VestingStatus[] = []

    for (const offer of offers || []) {
      const grantDate = new Date(offer.offer_date)
      const vestingStartDate = new Date(offer.offer_date)
      const schedule = offer.vesting_schedules

      if (!schedule) continue

      // Calculate cliff date
      const cliffDate = addMonths(vestingStartDate, schedule.cliff_months || 0)
      const cliffReached = isAfter(today, cliffDate) || today.getTime() === cliffDate.getTime()
      
      // Calculate days until cliff
      let daysUntilCliff = null
      if (!cliffReached) {
        daysUntilCliff = Math.ceil((cliffDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
      }

      // Calculate vested shares
      let vestedShares = 0
      let monthsVested = 0

      if (cliffReached) {
        monthsVested = Math.min(
          differenceInMonths(today, vestingStartDate),
          schedule.duration_months
        )

        const sharesOffered = offer.shares_offered || 0
        // Calculate based on vesting frequency
        if (schedule.vesting_frequency === 'monthly') {
          vestedShares = Math.floor((sharesOffered / schedule.duration_months) * monthsVested)
        } else if (schedule.vesting_frequency === 'quarterly') {
          const quartersVested = Math.floor(monthsVested / 3)
          vestedShares = Math.floor((sharesOffered / (schedule.duration_months / 3)) * quartersVested)
        } else if (schedule.vesting_frequency === 'yearly') {
          const yearsVested = Math.floor(monthsVested / 12)
          vestedShares = Math.floor((sharesOffered / (schedule.duration_months / 12)) * yearsVested)
        }
      }

      // Calculate next vesting date
      let nextVestingDate = null
      if (monthsVested < schedule.duration_months) {
        if (schedule.vesting_frequency === 'monthly') {
          nextVestingDate = format(addMonths(vestingStartDate, monthsVested + 1), 'yyyy-MM-dd')
        } else if (schedule.vesting_frequency === 'quarterly') {
          const nextQuarter = Math.ceil((monthsVested + 1) / 3) * 3
          nextVestingDate = format(addMonths(vestingStartDate, nextQuarter), 'yyyy-MM-dd')
        } else if (schedule.vesting_frequency === 'yearly') {
          const nextYear = Math.ceil((monthsVested + 1) / 12) * 12
          nextVestingDate = format(addMonths(vestingStartDate, nextYear), 'yyyy-MM-dd')
        }
      }

      const vestingEndDate = addMonths(vestingStartDate, schedule.duration_months)
      const isFullyVested = monthsVested >= schedule.duration_months

      const totalSharesOffered = offer.shares_offered || 0
      const monthlyVestingRate = totalSharesOffered / schedule.duration_months

      vestingStatuses.push({
        user_id: offer.user_id || '',
        user_name: `${offer.users?.first_name || ''} ${offer.users?.last_name || ''}`.trim() || offer.users?.email || 'Unknown',
        email: offer.users?.email || '',
        total_shares: totalSharesOffered,
        vested_shares: vestedShares,
        unvested_shares: totalSharesOffered - vestedShares,
        vesting_percentage: totalSharesOffered > 0 ? (vestedShares / totalSharesOffered) * 100 : 0,
        grant_date: format(grantDate, 'yyyy-MM-dd'),
        vesting_start_date: format(vestingStartDate, 'yyyy-MM-dd'),
        cliff_date: format(cliffDate, 'yyyy-MM-dd'),
        cliff_reached: cliffReached,
        days_until_cliff: daysUntilCliff,
        next_vesting_date: nextVestingDate,
        vesting_end_date: format(vestingEndDate, 'yyyy-MM-dd'),
        is_fully_vested: isFullyVested,
        vesting_schedule: schedule as VestingSchedule,
        monthly_vesting_rate: monthlyVestingRate
      })
    }

    return vestingStatuses
  }

  async getVestingMilestones(
    userId: string,
    offerId?: string
  ): Promise<VestingMilestone[]> {
    const statuses = await this.calculateVestingStatus(userId)
    
    if (statuses.length === 0) return []

    // Get the first status (or filter by offerId if provided)
    const status = statuses[0]
    const milestones: VestingMilestone[] = []

    const vestingStartDate = new Date(status.vesting_start_date)
    const schedule = status.vesting_schedule
    const today = new Date()

    const cliffMonths = schedule.cliff_months || 0
    // Add cliff milestone
    if (cliffMonths > 0) {
      const cliffDate = addMonths(vestingStartDate, cliffMonths)
      const cliffShares = Math.floor((status.total_shares / schedule.duration_months) * cliffMonths)
      
      milestones.push({
        date: format(cliffDate, 'yyyy-MM-dd'),
        shares_vesting: cliffShares,
        cumulative_vested_shares: cliffShares,
        percentage_vested: (cliffShares / status.total_shares) * 100,
        is_cliff: true,
        is_completed: isAfter(today, cliffDate) || today.getTime() === cliffDate.getTime()
      })
    }

    // Add regular vesting milestones
    let currentDate = addMonths(vestingStartDate, cliffMonths)
    let cumulativeShares = Math.floor((status.total_shares / schedule.duration_months) * cliffMonths)

    while (currentDate <= addMonths(vestingStartDate, schedule.duration_months)) {
      let incrementMonths = 1
      if (schedule.vesting_frequency === 'quarterly') {
        incrementMonths = 3
      } else if (schedule.vesting_frequency === 'yearly') {
        incrementMonths = 12
      }

      currentDate = addMonths(currentDate, incrementMonths)
      
      if (currentDate > addMonths(vestingStartDate, schedule.duration_months)) {
        break
      }

      const sharesThisPeriod = Math.floor((status.total_shares / schedule.duration_months) * incrementMonths)
      cumulativeShares += sharesThisPeriod

      milestones.push({
        date: format(currentDate, 'yyyy-MM-dd'),
        shares_vesting: sharesThisPeriod,
        cumulative_vested_shares: cumulativeShares,
        percentage_vested: (cumulativeShares / status.total_shares) * 100,
        is_cliff: false,
        is_completed: isAfter(today, currentDate) || today.getTime() === currentDate.getTime()
      })
    }

    return milestones
  }

  async simulateAcceleratedVesting(
    userId: string,
    triggerEvent: 'acquisition' | 'ipo' | 'termination',
    accelerationPercentage: number = 100
  ): Promise<VestingStatus[]> {
    const currentStatuses = await this.calculateVestingStatus(userId)
    
    return currentStatuses.map(status => {
      const additionalShares = Math.floor(
        (status.unvested_shares * accelerationPercentage) / 100
      )
      
      return {
        ...status,
        vested_shares: status.vested_shares + additionalShares,
        unvested_shares: status.unvested_shares - additionalShares,
        vesting_percentage: ((status.vested_shares + additionalShares) / status.total_shares) * 100,
        accelerated_vesting: {
          trigger_event: triggerEvent,
          acceleration_percentage: accelerationPercentage,
          additional_vested_shares: additionalShares
        }
      }
    })
  }

  async exportVestingSchedule(format: 'csv' | 'pdf' = 'csv'): Promise<string | Blob> {
    const statuses = await this.calculateVestingStatus()
    
    if (format === 'csv') {
      const headers = [
        'Name',
        'Email',
        'Total Shares',
        'Vested Shares',
        'Unvested Shares',
        'Vesting %',
        'Grant Date',
        'Cliff Date',
        'Cliff Reached',
        'Days Until Cliff',
        'Next Vesting Date',
        'Vesting End Date',
        'Fully Vested',
        'Schedule',
        'Monthly Rate'
      ]

      const rows = statuses.map(status => [
        status.user_name,
        status.email,
        status.total_shares,
        status.vested_shares,
        status.unvested_shares,
        `${status.vesting_percentage.toFixed(2)}%`,
        status.grant_date,
        status.cliff_date,
        status.cliff_reached ? 'Yes' : 'No',
        status.days_until_cliff || 'N/A',
        status.next_vesting_date || 'N/A',
        status.vesting_end_date,
        status.is_fully_vested ? 'Yes' : 'No',
        status.vesting_schedule.schedule_name,
        status.monthly_vesting_rate.toFixed(0)
      ])

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n')

      return csvContent
    }

    // PDF export would require additional library
    throw new Error('PDF export not implemented')
  }

  // Get aggregate vesting statistics
  async getVestingStatistics() {
    const statuses = await this.calculateVestingStatus()
    
    const totalShares = statuses.reduce((sum, s) => sum + s.total_shares, 0)
    const vestedShares = statuses.reduce((sum, s) => sum + s.vested_shares, 0)
    const unvestedShares = statuses.reduce((sum, s) => sum + s.unvested_shares, 0)
    
    const fullyVestedCount = statuses.filter(s => s.is_fully_vested).length
    const activeVestingCount = statuses.filter(s => !s.is_fully_vested).length
    const preCliffCount = statuses.filter(s => !s.cliff_reached).length
    
    return {
      total_participants: statuses.length,
      total_shares: totalShares,
      vested_shares: vestedShares,
      unvested_shares: unvestedShares,
      overall_vesting_percentage: totalShares > 0 ? (vestedShares / totalShares) * 100 : 0,
      fully_vested_count: fullyVestedCount,
      active_vesting_count: activeVestingCount,
      pre_cliff_count: preCliffCount,
      average_vesting_percentage: statuses.length > 0 
        ? statuses.reduce((sum, s) => sum + s.vesting_percentage, 0) / statuses.length 
        : 0
    }
  }
}